import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Stack,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import apiService from '../../services/api.service';
import type { WarehouseMode, SourceDocSummary, SourceDocHeader, SourceDocType } from '../../types/warehouse';

interface SourceDocumentPickerProps {
  companyId: number;
  mode: WarehouseMode;
  selected: SourceDocHeader | null;
  onSelect: (doc: SourceDocHeader | null) => void;
}

const docTypeFor = (mode: WarehouseMode): SourceDocType =>
  mode === 'Receiving' ? 'PurchaseOrder' : 'SalesOrder';

/**
 * Step 0 — anchor the session to one open source document (open PO for
 * Receiving, open SO for Delivery) read from the existing -pr-po synced tables.
 */
export default function SourceDocumentPicker({ companyId, mode, selected, onSelect }: SourceDocumentPickerProps): JSX.Element {
  const [options, setOptions] = useState<SourceDocSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [manualNo, setManualNo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [includeReceived, setIncludeReceived] = useState(false);
  const docType = docTypeFor(mode);
  const label = mode === 'Receiving' ? 'Purchase Order' : 'Sales Order';

  const loadList = useCallback(
    async (search?: string) => {
      if (!companyId) return;
      setLoading(true);
      setError(null);
      try {
        const res: any = await apiService.getWarehouseDocuments(companyId, mode, search, includeReceived);
        setOptions(Array.isArray(res?.data) ? res.data : []);
      } catch (e: any) {
        setError(e?.response?.data?.message ?? 'Failed to load open documents');
      } finally {
        setLoading(false);
      }
    },
    [companyId, mode, includeReceived]
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadDoc = useCallback(
    async (docEntry?: number, docNum?: string) => {
      setLoading(true);
      setError(null);
      try {
        const res: any = await apiService.getWarehouseDocumentLines(companyId, docType, { docEntry, docNum });
        if (res?.data) onSelect(res.data as SourceDocHeader);
        else setError('Document not found in the local sync layer');
      } catch (e: any) {
        setError(e?.response?.data?.message ?? 'Failed to load document lines');
      } finally {
        setLoading(false);
      }
    },
    [companyId, docType, onSelect]
  );

  if (selected) {
    const scannableLines = selected.lines.length;
    return (
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip color="primary" label={`${label} ${selected.docNum}`} />
        <Typography variant="body2">
          {selected.cardName || selected.cardCode}
          {selected.docDate ? ` · ${selected.docDate}` : ''}
        </Typography>
        <Chip size="small" variant="outlined" label={`${scannableLines} line${scannableLines === 1 ? '' : 's'}`} />
        <Button size="small" onClick={() => onSelect(null)}>
          Change document
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1">Step 0 — select the {label} to scan against</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 260 }}
          options={options}
          loading={loading}
          getOptionLabel={(o) => `${o.docNum} — ${o.cardName ? `${o.cardName} · ${o.cardCode}` : o.cardCode}`}
          onInputChange={(_e, v, reason) => {
            if (reason === 'input' && v.length >= 2) void loadList(v);
          }}
          onChange={(_e, v) => {
            if (v) void loadDoc(v.docEntry, v.docNum);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={includeReceived ? `${label}s (incl. received)` : `Open ${label}s`}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Typography variant="body2" color="text.secondary">
          or
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label={`${label} No.`}
            value={manualNo}
            onChange={(e) => setManualNo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manualNo.trim()) void loadDoc(undefined, manualNo.trim());
            }}
          />
          <Button variant="outlined" disabled={!manualNo.trim()} onClick={() => loadDoc(undefined, manualNo.trim())}>
            Load
          </Button>
        </Stack>
      </Stack>
      {mode === 'Receiving' && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeReceived}
              onChange={(e) => setIncludeReceived(e.target.checked)}
            />
          }
          label="Include received POs (to view their GRPOs)"
        />
      )}
      {error && (
        <Box>
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
